import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import styled from 'styled-components';
import { get } from 'lodash';
import { PersonAdd } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import { CONTRAST } from '../../../theme';

import { Picker, MarginButton, Input } from '../../../ui';
import { Header } from './ui';
import { Filter } from '../../../filters';

const TitleDiv = styled.div`
  margin: 0 0 8px 0;
  font-weight: 500;
  font-size: 18px;
  color: ${CONTRAST};
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  padding-left: 35px;
`;

const LoadingDiv = styled.div`
display: flex;
flex-direction: column;
padding-bottom: 8px;
`;

const LoadingHeader = styled.div`
  cursor: pointer;
  font-size: 16px;
  color: ${CONTRAST};
  font-weight: 500;
  padding-bottom: 8px;
  padding-right: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const NameInput = styled(Input)`
  box-sizing: content-box;
  margin-right: 16px;
  margin-bottom: 12px;
`;

const mapState = ({
  newCustomLayers,
  filters,
  customLayers,
  customLayersConfig,
  fetchingFilters,
  isPublic,
}) => {
  const newLayers = Object.values(newCustomLayers);
  if (!newLayers.length) return {};
  const customLayer = newLayers[0];

  const filteredCustomLayers = Object.values(customLayers).filter(
    // eslint-disable-next-line eqeqeq
    ({ source }) => customLayer.source == source,
  );

  return {
    isPublic,
    customLayer,
    filters,
    key: customLayer.key,
    filtersSelected: customLayer.filters,
    defaultStyle: customLayersConfig.defaultStyle,
    savingEnabled: !customLayersConfig.disablelayerSaving,
    hasMultiSources: get(filters, '[0].filters.length', false),
    customLayersForPicker: [
      {
        label: 'Copy existing layer',
        value: 'EMPTY',
      },
      ...filteredCustomLayers.map(({ key, label }) => ({
        label,
        value: key,
      })),
    ],
    customLayers,
    hasCustomLayers: !!filteredCustomLayers.length,
    fetchingFilters,
  };
};

const PickerContainer = styled.div`
  color: ${CONTRAST};
  padding-bottom: 16px;
  width: 100%;
`;

export const ChooseFilters = () => {
  const {
    key,
    filters,
    filtersSelected,
    customLayer,
    customLayers,
    hasCustomLayers,
    customLayersForPicker,
    defaultStyle,
    savingEnabled,
    hasMultiSources,
    fetchingFilters,
    isPublic,
  } = useSelector(mapState);

  const dispatch = useDispatch();
  const isNew = !customLayer.isCustom;
  const clear = () => {
    dispatch({
      type: 'CLEAR_CUSTOM_LAYER_FILTERS',
      payload: { key },
    });
    dispatch({ type: 'DRAW_DISABLED' });
    dispatch({ type: 'CLEAR_DRAW_AREA' });
  };

  const onChange = (value, filterKey, operator) => {
    dispatch({
      type: 'TRACK',
      payload: {
        name: 'Filter Selected',
        value,
        column: key,
        operator,
      },
    });
    dispatch({
      type: 'UPDATE_CUSTOM_LAYER_FILTER',
      payload: {
        key,
        filter: {
          operator,
          key: filterKey,
          value,
        },
      },
    });
  };

  const setFilterSource = useCallback(
    (source, dataKey, forceDots) => {
      dispatch({
        type: 'UPDATE_CUSTOM_LAYER_SOURCE',
        payload: {
          key,
          source,
          dataKey,
          forceDots,
        },
      });
    },
    [dispatch, key],
  );

  useEffect(() => {
    if ((filters || []).length === 1 && hasMultiSources && !customLayer.source) {
      const filter = filters[0];
      setFilterSource(filter.source, filter.dataKey, filter.forceDots);
    }
  }, [customLayer.source, filters, hasMultiSources, setFilterSource]);

  const [editedName, setEditedName] = useState(customLayer.label || '');
  const [onChangeDone] = useDebouncedCallback((n) => {
    dispatch({
      type: 'UPDATE_CUSTOM_LAYER_NAME',
      payload: {
        key,
        label: n,
      },
    });
  }, 200);

  const onNameChange = (e) => {
    setEditedName(e.target.value);
    onChangeDone(e.target.value);
  };

  const onSelectExistingLayer = (existingKey) => {
    const layer = customLayers[existingKey];

    if (existingKey === 'EMPTY') {
      dispatch({
        type: 'REPLACE_NEW_CUSTOM_LAYER',
        payload: {
          key,
          layer: {
            key,
            filters: {},
            label: editedName,
            style: defaultStyle,
            source: customLayer.source,
            dataKey: customLayer.dataKey,
          },
        },
      });
      return;
    }

    const newLabel = `${layer.label} copy`;
    setEditedName(newLabel);
    dispatch({
      type: 'REPLACE_NEW_CUSTOM_LAYER',
      payload: {
        key,
        layer: {
          key,
          filters: layer.filters,
          label: newLabel,
          style: layer.style,
          source: customLayer.source,
          dataKey: layer.dataKey,
        },
      },
    });
  };

  if (hasMultiSources && !customLayer.source) {
    return (
      <FilterList>
        <Header>Generate map layers and impact reports for:</Header>
        {filters.map((filter) => (
          <MarginButton
            active
            key={`${filter.source}-${filter.dataKey}`}
            onClick={() => setFilterSource(filter.source, filter.dataKey, filter.forceDots)}
          >
            {filter.label}
          </MarginButton>
        ))}
      </FilterList>
    );
  }

  if (fetchingFilters) {
    return (
      <FilterList>
        <LoadingDiv>
          <LoadingHeader>Loading</LoadingHeader>
        </LoadingDiv>
      </FilterList>
    );
  }

  if (!savingEnabled) {
    return (
      <FilterList>
        <Header>Filter Parcels</Header>
        <Filters
          filters={hasMultiSources
            ? filters.find(({ source }) => source === customLayer.source).filters
            : filters}
          filtersSelected={filtersSelected}
          onChange={onChange}
        />
        <MarginButton
          active={Object.keys(filtersSelected).length > 0}
          onClick={clear}
        >
          Clear Filters
        </MarginButton>
      </FilterList>
    );
  }

  return (
    <FilterList>
      <TitleDiv>
        <span>Custom Layer</span>
        {isPublic ? null : (
          <Tooltip
            title="Share Layer"
            aria-label="share-layer"
            key="share-layer"
            placement="right"
            arrow
          >
            <PersonAdd
              style={{
                cursor: 'pointer',
              }}
              onClick={() => {
                dispatch({
                  type: 'OPEN_SHARE_LAYER',
                });
                dispatch({
                  type: 'ADD_SHARE_LAYER_USERS',
                  payload: { users: customLayer.users },
                });
              }}
            />
          </Tooltip>
        )}
      </TitleDiv>
      <NameInput
        onChange={onNameChange}
        placeholder="Layer Name"
        value={editedName}
      />
      {isNew && hasCustomLayers
        ? [
          <PickerContainer key="copy-layer-picker">
            <Picker
              options={customLayersForPicker}
              onChange={(e) => onSelectExistingLayer(e.target.value)}
            />
          </PickerContainer>,
        ]
        : null}
      <MarginButton
        active={Object.keys(filtersSelected).length > 0}
        onClick={() => {
          dispatch({
            type: 'GET_FILTER_BBOX',
            payload: {
              config: {
                layer: customLayer,
              },
            },
          });
        }}
      >
        Zoom to Selection
      </MarginButton>
      <Header>Pick Layer Filters</Header>
      <Filters
        filters={hasMultiSources
          ? filters.find(({ source }) => source === customLayer.source).filters
          : filters}
        filtersSelected={filtersSelected}
        onChange={onChange}
      />
      <MarginButton
        active={Object.keys(filtersSelected).length > 0}
        onClick={clear}
      >
        Clear Filters
      </MarginButton>
    </FilterList>
  );
};

const FilterWrapper = styled.div`
  padding: 6px 0 0 6px;
  margin-right: 6px;
  margin-bottom: 6px;
  border-radius: 6px;
  box-shadow: ${({ isTitle }) => (isTitle ? 'none' : '2px 3px 1px #a5a5a5')};
  border: ${({ isTitle }) => (isTitle ? 'none' : '1px solid #efefef')};
  &:empty {
    display: none;
  }
`;

const Filters = ({ filters, filtersSelected, onChange }) => {
  const [query, setQuery] = useState('');
  const displayFilters = !query
    ? filters
    : filters.filter((filter) => {
      if (filter.type === 'TITLE') return true;
      return filter.label.toLowerCase().includes(query.toLowerCase());
    });
  return (
    <div>
      <TextField
        placeholder="Search for a Filter"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        variant="outlined"
        style={{ marginTop: 6, marginBottom: 6 }}
        size="small"
      />
      {displayFilters.map((filter) => (
        <FilterWrapper
          key={filter.key + filter.label}
          isTitle={filter.type === 'TITLE'}
        >
          <Filter
            filtersSelected={filtersSelected}
            onChange={onChange}
            filterKey={filter.key}
            label={filter.label}
            options={filter.options}
            type={filter.type}
            min={filter.min}
            max={filter.max}
            unit={filter.unit}
            operator={filter.operator}
          />
        </FilterWrapper>
      ))}
    </div>
  );
};
